import { ChargeType } from './charges'
import { InvoiceShiftRangeInclusionMethod } from './companies'
import { EmploymentType } from './employmentType'
import { Money } from './money'
import { ShiftInvoicedStatus } from './shifts'
import { StringBoolean } from './string-boolean'

export enum LineItemType {
  SHIFT = 'SHIFT',
  WORKER_SHIFT = 'WORKER_SHIFT',
  WORKER = 'WORKER',
  PENALTY = 'PENALTY',
  BONUS = 'BONUS',
  BONUS_TIME = 'BONUS_TIME',
  CREDIT = 'CREDIT',
  DRUG_SCREENING_FEE = 'DRUG_SCREENING_FEE',

  BONUS_PAY = 'BONUS_PAY',
  WORKER_REIMBURSEMENT_FEE = 'WORKER_REIMBURSEMENT_FEE',
  WORKER_QUALITY_CREDIT = 'WORKER_QUALITY_CREDIT',
  WORKER_DAMAGE_CREDIT = 'WORKER_DAMAGE_CREDIT',
  TRAINING_CREDIT = 'TRAINING_CREDIT',
  LATE_CANCEL_CREDIT = 'LATE_CANCEL_CREDIT',
  OVERBOOK_CREDIT = 'OVERBOOK_CREDIT',
  MARKUP_CREDIT = 'MARKUP_CREDIT',
  BUSINESS_REFERRAL_CREDIT = 'BUSINESS_REFERRAL_CREDIT',
  CONVERSION_FEE = 'CONVERSION_FEE',
  DIRECT_HIRE_PLACEMENT_FEE = 'DIRECT_HIRE_PLACEMENT_FEE',
  TRANSPORTATION_FEE = 'TRANSPORTATION_FEE',
  TRABA_REIMBURSEMENT_FEE = 'TRABA_REIMBURSEMENT_FEE',
}

export enum InvoiceStatus {
  DELETED = 'deleted',
  DRAFT = 'draft',
  OPEN = 'open',
  PARTIALLY_PAID = 'partially_paid',
  PAID = 'paid',
  VOID = 'void',
  UNCOLLECTIBLE = 'uncollectible',
  PAYMENT_ACTION_REQUIRED = 'payment_action_required',
  PAYMENT_FAILED = 'payment_failed',
  PAYMENT_PROCESSING = 'payment_processing',
}

export enum InvoicingTeam {
  CUSTOMER_OPS = 'CUSTOMER_OPS',
  CENTRAL_OPS = 'CENTRAL_OPS',
  SCALED_SYSTEMS = 'SCALED_SYSTEMS',
}

export enum CreditNoteStatus {
  DRAFT = 'DRAFT',
  ISSUED = 'ISSUED',
  VOID = 'VOID',
}

export enum InvoiceVoidType {
  INCORRECT_VALUES = 'INCORRECT_VALUES',
  MISSING_LINE_ITEM = 'MISSING_LINE_ITEM',
  INCORRECT_INVOICE_GROUP_OR_LOCATIONS = 'INCORRECT_INVOICE_GROUP_OR_LOCATIONS',
  TIMESHEETS = 'TIMESHEETS',
  MISSING_ADJUSTMENT = 'MISSING_ADJUSTMENT',
  INCORRECT_INVOICE_CYCLE_END = 'INCORRECT_INVOICE_CYCLE_END',
}

export const InvoiceVoidTypeDisplayNames: Record<InvoiceVoidType, string> = {
  [InvoiceVoidType.INCORRECT_VALUES]: 'Incorrect Values',
  [InvoiceVoidType.MISSING_LINE_ITEM]: 'Missing Line Item',
  [InvoiceVoidType.INCORRECT_INVOICE_GROUP_OR_LOCATIONS]:
    'Incorrect Invoice Group or Locations',
  [InvoiceVoidType.TIMESHEETS]: 'Timesheets',
  [InvoiceVoidType.MISSING_ADJUSTMENT]: 'Missing Adjustment',
  [InvoiceVoidType.INCORRECT_INVOICE_CYCLE_END]: 'Incorrect Invoice Cycle End',
}

export type VoidInvoiceDto = {
  voidReason: InvoiceVoidType
  voidNote?: string
}

export interface StripeInvoiceMetadata {
  invoiceId?: string
  invoiceGroupId?: string
  shiftIds?: string
}

export enum SupplementalInvoiceAdjustmentReason {
  UNBILLED_HOURS = 'Unbilled hours',
  CORRECTED_REGULAR_HOURS = 'Corrected regular hours',
  CORRECTED_OVERTIME_HOURS = 'Corrected overtime hours',
  CORRECTED_BILL_RATE = 'Corrected bill rate',
  INCORRECT_ROLE = 'Incorrect role',
  RETROACTIVE_ADJUSTMENT = 'Retroactive adjustment',
  WORKER_ADDED_AFTER_INVOICE_GENERATION = 'Worker added after invoice generation',
  SHIFT_CORRECTION_AFTER_INVOICE_GENERATION = 'Shift correction after invoice generation',
  OTHER_BILLING_ADJUSTMENT = 'Other billing adjustment',
}

export type SupplementalInvoiceNote = {
  title: string
  body?: string
}

export type CustomField = {
  name: string
  value: string
}

export interface LineItem {
  workerId?: string
  shiftId?: string
  shiftIds?: string[]
  type:
    | LineItemType.SHIFT
    | LineItemType.WORKER_SHIFT
    | LineItemType.WORKER
    | LineItemType.PENALTY
    | LineItemType.BONUS
    | LineItemType.CREDIT
    | LineItemType.DRUG_SCREENING_FEE
  chargeToBusiness: Money
  date: Date
  description: string
  stripeDescription: string
  stripeItemId?: string
  grossPayToWorker?: Money
  totalUnitsWorked?: number
  totalTimeWorked?: number
  chargeIds?: string[]
  creditNoteId?: string
}

export enum SortOrder {
  ASC = 'asc',
  DESC = 'desc',
}

export enum OrderBy {
  CREATED_AT = 'createdAt',
  UPDATED_AT = 'updatedAt',
  INVOICE_ID = 'invoiceId',
  COMPANY_ID = 'companyId',
  STATUS = 'status',
}

export type Pagination = {
  startAt?: number
  orderBy?: OrderBy
  sortOrder?: SortOrder
  limit?: number
}

export enum InvoiceResyncType {
  STRIPE_MISMATCH,
  INTERNAL_MISMATCH,
}

export interface Invoice {
  companyId: string
  invoiceId: string
  stripeInvoiceId?: string // Eg: in_1K4JRtJxJIrs34m7nQSlRDCF
  // The invoice number generated by stripe. Is not populated until an invoice is finalized
  stripeInvoiceNumber?: string // Eg: 988C859D-0004
  stripeMemo?: string
  rilletInvoiceId?: string
  invoiceNumber: string | undefined
  hostedInvoiceUrl?: string
  status: InvoiceStatus
  startDate?: Date
  endDate?: Date
  dueDate?: Date
  createdAt: Date
  totalCharge: Money
  lineItems: LineItem[]
  invoiceGroupId?: string
  invoiceGroupName?: string
  updatedAt?: Date
  paidAt?: Date
  regionIds?: string[]
  fromInvoice?: {
    id: string
    stripeId?: string
  }
  baseInvoiceId?: string // Reference relevant for invoice for fees added after the fact (i.e. late fees)
  disableLateFees?: boolean
  nextLateFeeDate?: Date
  customFields?: CustomField[]
  type?: LineItemType
  locationIds?: string[]
  requiresResync?: InvoiceResyncType
  employmentType: EmploymentType
  creditNotes: CreditNote[]
  shiftRangeInclusionMethod?: InvoiceShiftRangeInclusionMethod
  finalizedAt?: Date
  voidedAt?: Date
  voidNote?: string
  voidedRilletCreditMemoId: string | undefined
  voidedRilletCreditMemoNumber: string | undefined
  uncollectibleAt?: Date
  uncollectibleRilletCreditMemoId: string | undefined
  uncollectibleRilletCreditMemoNumber: string | undefined
  excludedShiftIds: string[]
}

export interface CreditNote {
  invoiceId: string
  lineItems: LineItem[]
  creditNoteId: string
  status: CreditNoteStatus
  creditNoteNumber?: string
  stripeCreditNoteNumber?: string
  stripeId?: string
  rilletCreditMemoId?: string
  rilletCreditMemoNumber?: string
}

export type CreateCreditNoteDto = {
  chargeIds?: string[]
  shiftIds?: string[]
  manualLineItems?: CreateCreditNoteManualLineItemDto[]
  createAsDraft?: boolean
  notifyCustomer?: boolean
}

export type CreateCreditNoteManualLineItemDto = {
  shiftId?: string
  type: LineItemType
  chargeToBusiness: Money
  date: string | Date
  description: string
  stripeDescription: string
}

export type VoidCreditNoteDto = {
  notifyCustomer: boolean
  invoiceId: string
}

export type IssueCreditNoteDto = {
  notifyCustomer: boolean
}

export type ResyncInvoiceDto = {
  formatByType?: FormatLineItemType
}

export type ReviseInvoiceDto = {
  shouldRegenerateLineItems?: boolean
  voidReason: InvoiceVoidType
  voidNote?: string
}

export type CreateLineItemDto = {
  shiftId?: string
  workerId?: string
  type: LineItemType
  chargeToBusiness: Money
  grossPayToWorker?: Money
  totalTimeWorked?: number
  totalUnitsWorked?: number
  date: string | Date
  description: string
  stripeDescription: string // TODO: Remove once backend deprecates
  stripeItemId?: string
  shiftIds?: string[]
}

export type AddShiftsToDraftInvoiceDto = {
  shiftIds: string[]
  allowPartiallyInvoicedShifts?: boolean
}

export type EditLineItemDto = {
  id: string
  stripeDescription?: string // TODO: Remove once backend deprecates
  description?: string
}

export type CreateInvoiceQuery = {
  createStripeInvoice: StringBoolean
  shouldGenerateLineItems: StringBoolean
  shouldValidate?: StringBoolean
  showWorkerLineItems?: StringBoolean
}

export enum FormatLineItemType {
  SHIFT = 'SHIFT',
  WORKER_SHIFT = 'WORKER_SHIFT',
  WORKER = 'WORKER',
}
// New Invoice Preview DTOs

export type LineItemPreviewDto = {
  shiftId?: string
  shiftIds?: string[]
  workerId?: string
  chargeIds?: string[]
  type: LineItemType
  chargeToBusiness: Money
  description: string
  stripeDescription: string // Used in Node Server but to be deprecated soon
}

export type InvalidShiftsForLineItemPreview = {
  shiftId: string
  reason: ShiftInvoiceIssueReason
}

export type LineItemsPreviewResponseDto = {
  lineItems: LineItemPreviewDto[]
  stripeInvoiceMemo: string
  invalidShifts: InvalidShiftsForLineItemPreview[]
}

export type ManuallyCreateInvoiceDto = {
  companyId: string
  formatByType: FormatLineItemType
  shiftIds: string[]
  invoiceGroupId?: string
}

export enum ShiftInvoiceIssueReason {
  OPEN_WORKER_SHIFTS = 'OPEN_WORKER_SHIFTS',
  INVOICED = 'INVOICED',
  NEEDS_MANUAL_ADJUSTMENT = 'NEEDS_MANUAL_ADJUSTMENT',
  MANUALLY_EXCLUDED = 'MANUALLY_EXCLUDED',
}

export type InvoiceShiftStatus = {
  shiftId: string
  invoicedStatus: ShiftInvoicedStatus
  shiftInvoiceIssue: ShiftInvoiceIssueReason | undefined
}

export type InvoiceShiftWorkerCharge = {
  workerId: string
  amount: Money
}

export type InvoiceShiftLevelCharge = {
  chargeType: ChargeType
  amount: Money
}

export type InvoiceShiftChargeSummary = {
  shiftId: string
  role: string
  startTime: Date
  endTime: Date
  timezone: string
  workerCharges: InvoiceShiftWorkerCharge[]
  shiftCharges: InvoiceShiftLevelCharge[]
  totalChargeOnInvoice: Money
}
